import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../services/api';
import { useDrillLevel } from '../components/common/drillLevel';
import StageModelsWidget from '../components/common/StageModelsWidget';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import type {
  ParserCandidate,
  SiteParser,
  SiteParserDetail,
  SiteParserSpecVersion,
  ParserBacktestResult,
  ParserChat,
} from '../types';

const SOURCES = ['BrowserPlugin', 'Serper', 'Crawl4ai'];

/** `?parser=<id>` — the open parser drawer. Settings reads it for its back button. */
export const PARSER_PARAM = 'parser';

// A minimal valid starter spec so "New parser" opens a working, editable draft.
function starterSpec() {
  return {
    version: 1,
    name: `new-parser-${Date.now()}`,
    kind: 'generic',
    match: { host_suffixes: ['example.com'], url_patterns: [] as string[] },
    fields: [{ name: 'title', selectors: ['h1'], required: false }],
  };
}

function statusColor(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-800';
    case 'DRAFT':
      return 'bg-gray-200 text-gray-800';
    case 'QUARANTINED':
      return 'bg-amber-100 text-amber-800';
    case 'DISABLED':
      return 'bg-red-100 text-red-800';
    case 'BUILDING':
      return 'bg-blue-100 text-blue-800';
    case 'BUILT':
      return 'bg-green-100 text-green-800';
    case 'SUGGESTED':
      return 'bg-indigo-100 text-indigo-800';
    default:
      return 'bg-gray-200 text-gray-800';
  }
}

export default function SiteParsers() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const [candidates, setCandidates] = useState<ParserCandidate[]>([]);
  const [parsers, setParsers] = useState<SiteParser[]>([]);
  // Which parser's drawer is open lives in the URL, not in state. Two reasons:
  // an open parser is now linkable/refreshable, and the models widget's return
  // trail (which captures path + query) can bring you back to the SAME drawer
  // after a trip to pipeline settings instead of dumping you on the list.
  //
  // It is also its own history entry (useDrillLevel), so Back — the browser's or
  // Settings' header — closes the drawer before it leaves Settings.
  const parserLevel = useDrillLevel(PARSER_PARAM);
  const selectedId = Number(parserLevel.value) || null;
  const setSelectedId = (id: number | null) =>
    id == null ? parserLevel.close() : parserLevel.open(String(id));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  // The user's active downloader — used to default the drawer's Focus source to what they
  // actually fetch with, so they build for that source by default.
  const [currentSource, setCurrentSource] = useState('');
  useEffect(() => {
    if (!isAdmin) return;
    api
      .getNetworkSettings()
      .then((s) => setCurrentSource(s.downloader_type || ''))
      .catch(() => {});
  }, [isAdmin]);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        api.listParserCandidates(),
        api.listSiteParsers(),
      ]);
      setCandidates(c);
      setParsers(p);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  // Live refresh: a re-captured sample or an AI (re)build fires `parser_updated`.
  const { subscribe } = useWebSocket();
  useEffect(() => {
    return subscribe((event: any) => {
      if (event?.event_type !== 'parser_updated') return;
      // A build that died on the LLM (context overflow, no credit, bad key) reports
      // here — the detail panel may not even be open, so show it at the list level.
      if (event.reason === 'build_failed' && event.message) setError(event.message);
      load();
    });
  }, [subscribe, load]);

  const refreshCandidates = async () => {
    setBusy('refresh');
    try {
      await api.refreshParserCandidates();
      setError('Discovery job queued - candidates will update shortly. Reload in a moment.');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const build = async (c: ParserCandidate) => {
    setBusy(`build-${c.id}`);
    try {
      const res = await api.buildParserCandidate(c.id);
      await load();
      setSelectedId(res.site_parser_id);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const dismiss = async (c: ParserCandidate) => {
    try {
      await api.dismissParserCandidate(c.id);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const newParser = async () => {
    setBusy('new');
    setError('');
    try {
      const d = await api.createSiteParser(starterSpec());
      await load();
      setSelectedId(d.id);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  if (!isAdmin) {
    return <div className="p-6 text-gray-600">Site parsers are admin-managed.</div>;
  }

  return (
    /* No `p-6 max-w-7xl` any more. This renders inside Settings ▸ Network,
       which is already inside Layout's card — so it was a max-w-7xl nested in a
       max-w-6xl nested in a max-w-7xl, padded three times over.

       An <h2>, not an <h1>: this is a panel inside a tab, and the page's <h1>
       is "Settings". It was also the only heading in the app with no `dark:`
       colour, so it stayed near-black on the dark background. */
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Site Parsers
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Deterministic per-site extractors, built from pages we keep crawling and shared
            across all users.
          </p>
        </div>
        {/* The models widget for this screen lives on the hub's sub-tab row
            (NetworkHub), top-right — not down here among the actions. */}
        <div className="flex gap-2">
          <button
            onClick={newParser}
            disabled={busy === 'new'}
            className="px-3 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy === 'new' ? 'Creating…' : '+ New parser'}
          </button>
          <button
            onClick={refreshCandidates}
            disabled={busy === 'refresh'}
            className="px-3 py-2 text-sm rounded border"
          >
            {busy === 'refresh' ? 'Queuing…' : 'Rescan for candidates'}
          </button>
        </div>
      </div>

      <div className="mb-4 p-3 rounded bg-blue-50 dark:bg-blue-950 text-blue-900 dark:text-blue-100 text-sm">
        <b>To try it:</b> click <b>+ New parser</b> (or <b>Build</b> a candidate) → the
        drawer walks you through 4 steps: add sample pages, define fields, <b>Test
        parser</b>, then activate. Candidates appear here once the discovery job has seen a
        URL shape enough times (needs an RQ worker + crawled pages).
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-amber-50 text-amber-800 text-sm flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="icon-btn font-bold">
            ×
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Candidates */}
        <section>
          <h2 className="text-lg font-semibold mb-2">
            Candidates <span className="text-gray-400 dark:text-gray-500">({candidates.length})</span>
          </h2>
          <div className="border dark:border-gray-700 rounded divide-y dark:divide-gray-700">
            {candidates.length === 0 && (
              <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
                No candidates yet. Click “Rescan for candidates”.
              </div>
            )}
            {candidates.map((c) => (
              <div key={c.id} className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono text-sm truncate">
                    {c.host}
                    <span className="text-gray-400 dark:text-gray-500">{c.url_template}</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    seen {c.encounter_count}× ·{' '}
                    <span className={`px-1.5 py-0.5 rounded ${statusColor(c.status)}`}>
                      {c.status}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {c.site_parser_id ? (
                    <button
                      onClick={() => setSelectedId(c.site_parser_id!)}
                      className="px-2 py-1 text-xs rounded border"
                    >
                      Open
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => build(c)}
                        disabled={busy === `build-${c.id}`}
                        className="px-2 py-1 text-xs rounded bg-indigo-600 text-white disabled:opacity-50"
                      >
                        {busy === `build-${c.id}` ? 'Building…' : 'Build'}
                      </button>
                      <button
                        onClick={() => dismiss(c)}
                        className="px-2 py-1 text-xs rounded border text-gray-500 dark:text-gray-400"
                      >
                        Dismiss
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Parsers */}
        <section>
          <h2 className="text-lg font-semibold mb-2">
            Parsers <span className="text-gray-400 dark:text-gray-500">({parsers.length})</span>
          </h2>
          <div className="border dark:border-gray-700 rounded divide-y dark:divide-gray-700">
            {parsers.length === 0 && (
              <div className="p-4 text-sm text-gray-500 dark:text-gray-400">No parsers yet.</div>
            )}
            {parsers.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className={`w-full text-left p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 ${
                  selectedId === p.id ? 'bg-indigo-50 dark:bg-indigo-900/40' : ''
                }`}
              >
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{p.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {p.host_suffix} · {p.kind} ·{' '}
                    {p.approved_for_sources.length
                      ? p.approved_for_sources.join(', ')
                      : 'no sources'}
                  </div>
                </div>
                {p.building ? (
                  <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-800 animate-pulse whitespace-nowrap">
                    ⏳ Building…
                  </span>
                ) : (
                  <span
                    className={`px-1.5 py-0.5 text-xs rounded ${statusColor(p.status)}`}
                  >
                    {p.status}
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      </div>

      {selectedId != null && (
        <ParserDetail
          parserId={selectedId}
          defaultSource={currentSource}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function ParserDetail({
  parserId,
  defaultSource,
  onClose,
  onChanged,
}: {
  parserId: number;
  defaultSource: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<SiteParserDetail | null>(null);
  const [specText, setSpecText] = useState('');
  const [backtest, setBacktest] = useState<ParserBacktestResult | null>(null);
  const [backtestMode, setBacktestMode] = useState<'held-out' | 'all'>('held-out');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [captureUrls, setCaptureUrls] = useState('');
  const [approveSources, setApproveSources] = useState<string[]>(['BrowserPlugin']);
  const [pasteUrl, setPasteUrl] = useState('');
  const [pasteHtml, setPasteHtml] = useState('');
  const [viewHtml, setViewHtml] = useState<{
    url: string;
    source: string;
    html: string;
  } | null>(null);
  const [htmlMode, setHtmlMode] = useState<'rendered' | 'source'>('rendered');
  // Focus the whole drawer (sample list, training selection, chat) on ONE network source.
  // The same URL renders differently per source, so a parser is really per-source — this
  // lets you build/refine against one source's pages without the others confusing things.
  // Defaults to the user's active downloader (defaultSource) so you build for what you fetch.
  const [focusSource, setFocusSource] = useState<string>(defaultSource || 'all');
  const focusTouchedRef = useRef(false);
  useEffect(() => {
    // Adopt the user's active downloader as the default focus once it loads — unless the
    // user has manually picked a focus.
    if (!focusTouchedRef.current && defaultSource) setFocusSource(defaultSource);
  }, [defaultSource]);
  // Samples added by hand (paste / by-URL) are tagged with the focused source — that's the
  // source you're building for. When focus is "All", pasted DOM is closest to the plugin.
  const captureSource = focusSource === 'all' ? 'BrowserPlugin' : focusSource;
  // Always offer the user's active source in the dropdown (even with no samples yet), plus
  // any source that has samples.
  const availableSources = Array.from(
    new Set(
      [defaultSource, ...(detail?.samples || []).map((s) => s.source)].filter(Boolean),
    ),
  );
  const shownSamples = (detail?.samples || []).filter(
    (s) => focusSource === 'all' || s.source === focusSource,
  );
  const usableRawCount = shownSamples.filter((s) => s.usable_raw).length;
  // Unsaved spec edits: Test/Activate operate on the SAVED spec, not the textarea, so we
  // guard them until Save spec is clicked. Compare canonical JSON so reformatting the
  // textarea (whitespace/key order) doesn't read as dirty; unparseable = definitely dirty.
  const specDirty = useMemo(() => {
    if (!detail) return false;
    try {
      return JSON.stringify(JSON.parse(specText)) !== JSON.stringify(detail.spec);
    } catch {
      return true;
    }
  }, [specText, detail]);
  // `load` runs from callbacks/intervals, so it can't close over `specDirty` without
  // going stale — mirror it (and the last spec we've shown) into refs.
  const specDirtyRef = useRef(false);
  const lastSavedSpecRef = useRef('');
  useEffect(() => {
    specDirtyRef.current = specDirty;
  }, [specDirty]);
  // True when the SAVED spec changed underneath unsaved edits (an agent round landed).
  const [savedSpecMoved, setSavedSpecMoved] = useState(false);
  // Which usable-raw samples the builder agent trains on. Defaults to the most-recent raw
  // ones (within the focused source); the user can adjust with the checkboxes.
  const [selectedSamples, setSelectedSamples] = useState<Set<number>>(new Set());
  const touchedSelRef = useRef(false);
  const aliveRef = useRef(true);

  // Auto-default the selection to the most-recent raw samples of the focused source
  // whenever the sample set or focus changes — unless the user manually adjusted it.
  useEffect(() => {
    if (touchedSelRef.current || !detail) return;
    const rawIds = detail.samples
      .filter((s) => s.usable_raw && (focusSource === 'all' || s.source === focusSource))
      .map((s) => s.id)
      .sort((a, b) => b - a) // most recent (highest id) first
      .slice(0, 4);
    setSelectedSamples(new Set(rawIds));
  }, [detail, focusSource]);

  const toggleSample = (id: number) => {
    touchedSelRef.current = true;
    setSelectedSamples((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // Refresh the parser. The editor is only overwritten when it has NO unsaved edits —
  // the agent and the user share this spec, and a round landing mid-edit used to wipe
  // whatever was typed. When they collide we keep the typing and raise a banner instead,
  // because the edits are the unrecoverable side.
  const load = useCallback(async () => {
    try {
      const d = await api.getSiteParser(parserId);
      if (!aliveRef.current) return d;
      setDetail(d);
      if (specDirtyRef.current) {
        const saved = JSON.stringify(d.spec);
        if (saved !== lastSavedSpecRef.current) {
          lastSavedSpecRef.current = saved;
          setSavedSpecMoved(true);
        }
      } else {
        lastSavedSpecRef.current = JSON.stringify(d.spec);
        setSavedSpecMoved(false);
        setSpecText(JSON.stringify(d.spec, null, 2));
      }
      return d;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [parserId]);

  // Load the saved spec into the editor, discarding local edits (the explicit escape
  // hatch from the banner above).
  const discardSpecEdits = useCallback(() => {
    if (!detail) return;
    setSpecText(JSON.stringify(detail.spec, null, 2));
    setSavedSpecMoved(false);
  }, [detail]);

  useEffect(() => {
    load();
  }, [load]);

  // Live refresh from the server (re-captured sample landed, or an AI build finished).
  // On a 'sample' event we refresh samples but LEAVE the spec editor alone (don't clobber
  // in-progress edits); on 'rebuilt' we pull the new spec into the editor.
  const { subscribe } = useWebSocket();
  useEffect(() => {
    return subscribe(async (event: any) => {
      if (event?.event_type !== 'parser_updated' || event.parser_id !== parserId) return;
      try {
        const d = await api.getSiteParser(parserId);
        if (!aliveRef.current) return;
        setDetail(d);
        if (event.reason === 'rebuilt') {
          // Same rule as load(): never discard unsaved edits behind the user's back.
          if (specDirtyRef.current) {
            lastSavedSpecRef.current = JSON.stringify(d.spec);
            setSavedSpecMoved(true);
          } else {
            lastSavedSpecRef.current = JSON.stringify(d.spec);
            setSpecText(JSON.stringify(d.spec, null, 2));
          }
          setError(event.message || 'AI rebuilt the spec - review it and Test parser.');
        } else if (event.reason === 'build_failed') {
          setError(event.message || 'AI build failed.');
        }
      } catch {
        /* ignore */
      }
    });
  }, [subscribe, parserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // While a build is running, poll as a fallback in case the completion event is missed.
  useEffect(() => {
    if (!detail?.building) return;
    const t = setInterval(() => {
      if (aliveRef.current) load();
    }, 5000);
    return () => clearInterval(t);
  }, [detail?.building, load]);

  const saveSpec = async () => {
    setBusy('spec');
    setError('');
    try {
      const spec = JSON.parse(specText);
      const d = await api.updateSiteParserSpec(parserId, spec);
      setDetail(d);
      lastSavedSpecRef.current = JSON.stringify(d.spec);
      setSavedSpecMoved(false); // the editor and the saved spec agree again
      setBacktest(null);
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const runPreview = async (full = false) => {
    setBusy(full ? 'preview-all' : 'preview');
    setError('');
    setBacktest(null); // drop stale results so Step 3 shows "testing…", not the old run
    try {
      setBacktest(
        await api.previewSiteParser(
          parserId,
          focusSource === 'all' ? undefined : focusSource,
          // Default Test holds out the samples we BUILT from → measures generalization.
          full
            ? { full: true }
            : { excludeIds: Array.from(selectedSamples) },
        ),
      );
      setBacktestMode(full ? 'all' : 'held-out');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  // Called when the builder chat finishes a turn. Reload the parser (refreshes Step 2), and
  // if the agent actually changed the spec, auto-run Step 3's test so results aren't stale —
  // the /preview back-tests the SAVED spec, which the chat already persisted, so this shows
  // the effect without the user hunting for the Test button.
  const specSnapshotRef = useRef('');
  useEffect(() => {
    specSnapshotRef.current = specText;
  }, [specText]);
  const handleBuilderDone = useCallback(async () => {
    const before = specSnapshotRef.current;
    const d = await load();
    const after = d ? JSON.stringify(d.spec, null, 2) : before;
    if (d && after !== before) await runPreview(false);
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const capture = async () => {
    setBusy('capture');
    setError('');
    try {
      const urls = captureUrls
        .split('\n')
        .map((u) => u.trim())
        .filter(Boolean);
      const d = await api.captureParserSamples(parserId, urls, captureSource);
      setDetail(d);
      setCaptureUrls('');
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const loadStored = async () => {
    setBusy('loadstored');
    setError('');
    try {
      const d = await api.loadStoredParserSamples(parserId, 12);
      setDetail(d);
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const openSampleHtml = async (sampleId: number) => {
    setError('');
    try {
      const s = await api.getSiteParserSampleHtml(parserId, sampleId);
      setHtmlMode('rendered');
      setViewHtml({ url: s.url, source: s.source, html: s.html });
    } catch (e: any) {
      setError(e.message);
    }
  };

  const rebuild = async () => {
    setBusy('rebuild');
    setError('');
    try {
      await api.rebuildSiteParser(parserId, Array.from(selectedSamples));
      setError(
        'AI build started - follow along in the builder chat below: it explores your ' +
          'samples, writes the fields and back-tests them (needs an RQ worker + a ' +
          'configured LLM). The spec updates here automatically when it finishes, and ' +
          'you can reply in the chat to refine it.',
      );
      await load(); // pick up build_started_at → shows "Building…" immediately
      onChanged(); // refresh the parent list's badge too
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const recapture = async (urls?: string[]) => {
    setBusy('recapture');
    setError('');
    try {
      const res = await api.recaptureParserSamplesViaBrowser(parserId, urls);
      setError(`Queued ${res.queued} browser fetch(es). ${res.note}`);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const fetchUrlsViaBrowser = async () => {
    const urls = captureUrls
      .split('\n')
      .map((u) => u.trim())
      .filter(Boolean);
    if (!urls.length) {
      setError('Add one or more URLs first.');
      return;
    }
    await recapture(urls);
  };

  const addPasted = async () => {
    setBusy('paste');
    setError('');
    try {
      const d = await api.addInlineParserSample(
        parserId,
        pasteUrl.trim() || 'https://example.com/pasted',
        pasteHtml,
        captureSource,
      );
      setDetail(d);
      setPasteHtml('');
      setPasteUrl('');
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const activate = async () => {
    setBusy('activate');
    setError('');
    try {
      const d = await api.activateSiteParser(parserId, approveSources);
      setDetail(d);
      onChanged();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const doAction = async (fn: () => Promise<any>) => {
    setError('');
    try {
      await fn();
      await load();
      onChanged();
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
      <div
        className="w-full max-w-4xl h-full bg-white dark:bg-gray-900 shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b dark:border-gray-700 p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold">{detail?.name || 'Parser'}</h2>
            {detail && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {detail.host_suffix} · {detail.kind} ·{' '}
                {detail.building ? (
                  <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 animate-pulse">
                    ⏳ Building… (AI is writing the parser - a few minutes)
                  </span>
                ) : (
                  <>
                    <span
                      className={`px-1.5 py-0.5 rounded ${statusColor(detail.status)}`}
                    >
                      {detail.status}
                    </span>{' '}
                    · approved: {detail.approved_for_sources.join(', ') || 'none'}
                  </>
                )}
              </div>
            )}
          </div>
          {/* Close on top, models under it — the drawer is where the parser
              work actually happens, so this is where "what model is doing
              this" belongs. Its links carry the ?parser= trail back, so
              returning from settings re-opens THIS drawer. */}
          <div className="flex flex-col items-end gap-2">
            <button onClick={onClose} className="icon-btn text-2xl">
              ×
            </button>
            <StageModelsWidget surface="site_parsers" returnLabel="this parser" />
          </div>
        </div>

        {error && (
          <div className="m-4 p-3 rounded bg-red-50 text-red-700 text-sm">{error}</div>
        )}

        {detail && (
          <div className="p-4 space-y-6">
            {/* How-to guide */}
            <div className="rounded bg-blue-50 dark:bg-blue-950 text-blue-900 dark:text-blue-100 text-xs p-3 leading-relaxed">
              <b>Build &amp; test a parser:</b> <b>1)</b> add sample pages,{' '}
              <b>2)</b> define the fields to extract, <b>3)</b> hit <b>Test parser</b> to
              see what it pulls from each sample, <b>4)</b> approve it for a network source
              to make it live. A parser is just a JSON spec - no code.
            </div>

            {/* Step 1 — Samples */}
            <section>
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold">
                  <span className="text-indigo-600">Step 1 ·</span> Samples - pages to test
                  against{' '}
                  <span className="text-gray-400 dark:text-gray-500 font-normal">
                    ({shownSamples.length}, {usableRawCount} usable for extraction)
                  </span>
                </h3>
                <div className="flex gap-2 items-center">
                  {availableSources.length > 0 && (
                    <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      Focus source:
                      <select
                        value={focusSource}
                        onChange={(e) => {
                          focusTouchedRef.current = true; // user's explicit choice sticks
                          touchedSelRef.current = false; // re-default selection to new source
                          setFocusSource(e.target.value);
                        }}
                        className="text-xs border dark:border-gray-700 rounded px-1 py-0.5"
                      >
                        <option value="all">All</option>
                        {availableSources.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <button
                    onClick={loadStored}
                    disabled={busy === 'loadstored'}
                    className="px-2 py-1 text-xs rounded bg-indigo-600 text-white disabled:opacity-50"
                  >
                    {busy === 'loadstored' ? 'Loading…' : 'Load crawled pages'}
                  </button>
                  <button
                    onClick={() => recapture()}
                    disabled={busy === 'recapture'}
                    className="px-2 py-1 text-xs rounded border disabled:opacity-50"
                    title="Re-fetch cleaned sample URLs through your browser to get raw HTML"
                  >
                    {busy === 'recapture' ? 'Queuing…' : 'Re-capture via browser'}
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                <b>Check</b> the raw samples the <b>builder agent</b> should learn from.
                Only <b>raw</b> HTML is testable - crawl4ai’s cleaned pages need{' '}
                <b>Re-capture via browser</b> or a paste to become usable.
              </p>
              {shownSamples.length === 0 ? (
                <div className="text-xs text-gray-400 dark:text-gray-500 border dark:border-gray-700 rounded p-3 mb-2">
                  {detail.samples.length === 0
                    ? 'No samples yet. Click Load crawled pages, or paste a page’s HTML below.'
                    : `No ${focusSource} samples - switch Focus source or add some.`}
                </div>
              ) : (
                <div className="text-xs space-y-1 mb-2 max-h-40 overflow-auto">
                  {shownSamples.map((s) => (
                    <div key={s.id} className="flex justify-between items-center border-b dark:border-gray-700 py-1 gap-2">
                      <label className="flex items-center gap-2 min-w-0 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedSamples.has(s.id)}
                          disabled={!s.usable_raw}
                          onChange={() => toggleSample(s.id)}
                          title={
                            s.usable_raw
                              ? 'Let the builder agent learn from this sample'
                              : 'Cleaned HTML - can’t train on it (re-capture for raw)'
                          }
                        />
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          className="truncate text-indigo-600 hover:underline"
                          title="Open the live page in a new tab"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {s.url}
                        </a>
                      </label>
                      <span className="shrink-0 flex items-center gap-2">
                        <button
                          onClick={() => openSampleHtml(s.id)}
                          className="text-indigo-600 hover:underline"
                        >
                          View HTML
                        </button>
                        <span className={s.usable_raw ? 'text-green-600' : 'text-gray-400 dark:text-gray-500'}>
                          {s.source} · {s.usable_raw ? 'raw ✓' : 'cleaned'}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {/* add more samples — tagged with the focused source */}
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Added samples are tagged{' '}
                <b>{captureSource}</b>
                {focusSource === 'all'
                  ? ' (set Focus source above to tag them differently)'
                  : ' (the focused source)'}
                .
              </div>
              <details className="mb-2">
                <summary className="text-xs text-indigo-600 cursor-pointer">
                  Add samples by URL
                </summary>
                <textarea
                  value={captureUrls}
                  onChange={(e) => setCaptureUrls(e.target.value)}
                  placeholder="One URL per line"
                  className="w-full h-16 text-xs border rounded p-2 mt-1"
                />
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={fetchUrlsViaBrowser}
                    disabled={busy === 'recapture'}
                    className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white disabled:opacity-50"
                    title="Fetch these URLs through your browser now (works even if never crawled)"
                  >
                    {busy === 'recapture' ? 'Queuing…' : 'Fetch via browser'}
                  </button>
                  <button
                    onClick={capture}
                    disabled={busy === 'capture'}
                    className="px-3 py-1.5 text-sm rounded border disabled:opacity-50"
                    title="Use already-crawled content for these URLs"
                  >
                    {busy === 'capture' ? 'Capturing…' : 'Use crawled content'}
                  </button>
                </div>
              </details>
              <details className="mb-2">
                <summary className="text-xs text-indigo-600 cursor-pointer">
                  Paste raw HTML (fastest way to a testable sample)
                </summary>
                <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-1 mb-1">
                  Browser View Source → copy. Raw HTML keeps the id/class selectors the
                  parser needs.
                </div>
                <input
                  value={pasteUrl}
                  onChange={(e) => setPasteUrl(e.target.value)}
                  placeholder="URL this HTML is for"
                  className="w-full text-xs border rounded px-2 py-1 mb-1"
                />
                <textarea
                  value={pasteHtml}
                  onChange={(e) => setPasteHtml(e.target.value)}
                  placeholder="Paste raw page HTML here"
                  className="w-full h-24 text-xs border rounded p-2 font-mono"
                />
                <button
                  onClick={addPasted}
                  disabled={busy === 'paste' || !pasteHtml.trim()}
                  className="mt-1 px-3 py-1.5 text-sm rounded border disabled:opacity-50"
                >
                  {busy === 'paste' ? 'Adding…' : 'Add pasted sample'}
                </button>
              </details>
            </section>

            {/* Step 2 — Spec */}
            <section>
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold">
                  <span className="text-indigo-600">Step 2 ·</span> Fields to extract (the
                  spec)
                </h3>
                {detail.building && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ⏳ the builder agent is working…
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Ask the <b>builder agent</b> to write the fields from your samples, or edit
                the spec by hand below. An empty <code>fields</code> list extracts nothing.
              </p>

              {/* The agent PRODUCES the spec, so it sits above the JSON it writes — this is
                  the primary way to fill Step 2, not an afterthought at the bottom. */}
              <BuilderChat
                parserId={parserId}
                focusSource={focusSource}
                onSpecMaybeChanged={handleBuilderDone}
                building={!!detail.building}
                hasFields={((detail.spec as any)?.fields || []).length > 0}
                selectedSampleCount={selectedSamples.size}
                usableRawCount={usableRawCount}
                onBuild={rebuild}
                buildBusy={busy === 'rebuild'}
              />

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 mb-1">
                The spec the agent is editing - yours to tweak by hand. Add entries to{' '}
                <code>fields</code> with CSS selectors you can see in the sample HTML.
              </p>
              {savedSpecMoved && (
                <div className="mb-1 p-2 rounded bg-amber-50 dark:bg-amber-950 text-amber-800 dark:text-amber-200 text-xs flex items-center gap-2">
                  <span className="flex-1">
                    The saved spec changed while you were editing (the builder agent wrote
                    a new version). Your edits below are untouched - <b>Save spec</b> to
                    keep them, or discard them to see what the agent wrote.
                  </span>
                  <button
                    onClick={discardSpecEdits}
                    className="px-2 py-0.5 rounded border border-amber-400 dark:border-amber-700 shrink-0"
                  >
                    Discard my edits
                  </button>
                </div>
              )}
              <textarea
                value={specText}
                onChange={(e) => setSpecText(e.target.value)}
                spellCheck={false}
                className="w-full h-64 font-mono text-xs border rounded p-2"
              />
              <SpecVersions
                parserId={parserId}
                currentSpec={detail.spec}
                onRestored={(d) => {
                  setDetail(d);
                  setSpecText(JSON.stringify(d.spec, null, 2));
                  lastSavedSpecRef.current = JSON.stringify(d.spec);
                  setSavedSpecMoved(false); // an explicit choice, not a clobber
                  onChanged();
                }}
                onPreview={(spec) => setSpecText(JSON.stringify(spec, null, 2))}
              />
              <div className="flex gap-2 mt-2 items-center">
                <button
                  onClick={saveSpec}
                  disabled={busy === 'spec' || !specDirty}
                  className={`px-3 py-1.5 text-sm rounded text-white disabled:opacity-50 ${
                    specDirty ? 'bg-amber-600' : 'bg-gray-400'
                  }`}
                  title={
                    specDirty
                      ? 'Persist your spec edits (Test and Activate use the saved spec)'
                      : 'No unsaved changes'
                  }
                >
                  {busy === 'spec'
                    ? 'Saving…'
                    : specDirty
                      ? 'Save spec *'
                      : 'Saved'}
                </button>
                <button
                  onClick={() => runPreview(false)}
                  disabled={busy === 'preview' || busy === 'preview-all' || specDirty}
                  className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white disabled:opacity-50"
                  title={
                    specDirty
                      ? 'Save spec first - Test runs against the saved spec'
                      : 'Fast check on samples the parser was NOT built from (held-out) - measures generalization'
                  }
                >
                  {busy === 'preview' ? 'Testing…' : '▶ Test parser'}
                </button>
                <button
                  onClick={() => runPreview(true)}
                  disabled={busy === 'preview' || busy === 'preview-all' || specDirty}
                  className="px-3 py-1.5 text-sm rounded border disabled:opacity-50"
                  title={
                    specDirty
                      ? 'Save spec first - Test runs against the saved spec'
                      : 'Parse every stored sample (slower - no cap)'
                  }
                >
                  {busy === 'preview-all'
                    ? 'Testing all…'
                    : `Test all${shownSamples.length ? ` (${shownSamples.length})` : ''}`}
                </button>
                {specDirty && (
                  <span className="text-xs text-amber-700 dark:text-amber-300">
                    Unsaved spec changes - Save spec before Test / Activate.
                  </span>
                )}
              </div>
            </section>

            {/* Step 3 — Test results: original vs output */}
            <section>
              <h3 className="font-semibold mb-1">
                <span className="text-indigo-600">Step 3 ·</span> Test results - what it
                extracts
              </h3>
              {busy === 'preview' || busy === 'preview-all' ? (
                <div className="text-xs text-gray-500 dark:text-gray-400 border dark:border-gray-700 rounded p-3">
                  Testing the saved spec against your samples…
                </div>
              ) : !backtest ? (
                <div className="text-xs text-gray-400 dark:text-gray-500 border dark:border-gray-700 rounded p-3">
                  Click <b>▶ Test parser</b> in Step 2 to run the spec against your samples
                  and see the extracted text here.
                </div>
              ) : (
                <>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                  {backtestMode === 'all'
                    ? 'Tested against all samples (including the ones you built from).'
                    : 'Tested against held-out samples (the ones you didn’t build from) - a truer generalization check. Use Test all for full coverage.'}
                </div>
                <div className="text-xs mb-2">
                  {Object.entries(backtest.by_source).map(([src, agg]) => (
                    <div key={src}>
                      <b>{src}</b>: matched {agg.url_matched}/{agg.total} URLs, extracted{' '}
                      {agg.extracted}/{agg.extraction_tested} parsed (raw {agg.usable_raw}),
                      avg fill {agg.avg_fill_ratio}
                      {agg.usable_raw > agg.extraction_tested && (
                        <span className="text-gray-400 dark:text-gray-500">
                          {' '}
                          · {agg.usable_raw - agg.extraction_tested} more raw not parsed (Test
                          is capped for speed)
                        </span>
                      )}
                    </div>
                  ))}
                  {backtest.sample_count === 0 && (
                    <div className="text-gray-500 dark:text-gray-400">No samples yet - add some in Step 1.</div>
                  )}
                  {backtest.sample_count > 0 &&
                    Object.values(backtest.by_source).every((a) => a.extracted === 0) && (
                      <div className="text-amber-700 dark:text-amber-300 mt-1">
                        Nothing extracted. Either the samples are cleaned (no selectors - see
                        Step 1) or the spec’s selectors don’t match the HTML. Save the spec
                        after edits, then Test again.
                      </div>
                    )}
                </div>
                {backtest.samples
                  .filter((s: any) => s.extraction_tested)
                  .map((s: any, i: number) => (
                  <div key={i} className="border dark:border-gray-700 rounded p-2 mb-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate">
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 hover:underline"
                      >
                        {s.url}
                      </a>{' '}
                      · {s.source} · {s.url_matched ? 'matched' : 'no match'} ·{' '}
                      {s.usable_raw ? 'raw' : 'cleaned'}
                    </div>
                    <div className="text-[11px] text-gray-400 dark:text-gray-500 mb-0.5">
                      extracted text (what the parser hands downstream)
                    </div>
                    <pre className="text-[11px] bg-gray-50 dark:bg-gray-800 p-2 rounded overflow-y-auto max-h-56 whitespace-pre-wrap break-words">
                      {s.projections?.render || '(no extraction - see note above)'}
                    </pre>
                    <details className="mt-1">
                      <summary className="text-[11px] text-gray-400 dark:text-gray-500 cursor-pointer">
                        structured fields (debug - not consumed downstream)
                      </summary>
                      <pre className="text-[11px] bg-gray-50 dark:bg-gray-800 p-2 rounded overflow-y-auto max-h-48 whitespace-pre-wrap break-words mt-1">
                        {s.structured
                          ? JSON.stringify(s.structured.fields, null, 1)
                          : '(none)'}
                      </pre>
                    </details>
                  </div>
                ))}
                </>
              )}
            </section>

            {/* Step 4 — Activate per source */}
            <section>
              <h3 className="font-semibold mb-1">
                <span className="text-indigo-600">Step 4 ·</span> Approve &amp; activate
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                A parser only applies for the network sources it’s approved for (the same
                URL renders differently per source).
              </p>
              {specDirty && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mb-2">
                  You have unsaved spec edits. Activate approves the <b>saved</b> spec - Save
                  spec in Step 2 first, then Activate.
                </p>
              )}
              <div className="flex gap-3 mb-2">
                {SOURCES.map((s) => (
                  <label key={s} className="text-sm flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={approveSources.includes(s)}
                      onChange={(e) =>
                        setApproveSources((prev) =>
                          e.target.checked ? [...prev, s] : prev.filter((x) => x !== s),
                        )
                      }
                    />
                    {s}
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={activate}
                  disabled={
                    busy === 'activate' || approveSources.length === 0 || specDirty
                  }
                  className="px-3 py-1.5 text-sm rounded bg-green-600 text-white disabled:opacity-50"
                  title={
                    specDirty
                      ? 'Save spec first - Activate approves the saved spec'
                      : 'Approve the saved spec for the checked sources and go live'
                  }
                >
                  {busy === 'activate' ? 'Activating…' : 'Activate for selected'}
                </button>
                {detail.status === 'ACTIVE' && (
                  <button
                    onClick={() => doAction(() => api.quarantineSiteParser(parserId))}
                    className="px-3 py-1.5 text-sm rounded border text-amber-700 dark:text-amber-300"
                  >
                    Quarantine
                  </button>
                )}
                <button
                  onClick={() => doAction(() => api.disableSiteParser(parserId))}
                  className="px-3 py-1.5 text-sm rounded border text-red-700"
                >
                  Disable
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Delete this parser?'))
                      doAction(async () => {
                        await api.deleteSiteParser(parserId);
                        onClose();
                      });
                  }}
                  className="px-3 py-1.5 text-sm rounded border text-gray-500 dark:text-gray-400"
                >
                  Delete
                </button>
              </div>
            </section>
          </div>
        )}
      </div>

      {viewHtml && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
          onClick={() => setViewHtml(null)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded shadow-xl w-full max-w-5xl h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3 border-b dark:border-gray-700">
              <div className="text-sm min-w-0">
                <div className="font-semibold truncate">{viewHtml.url}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {viewHtml.source} · {(viewHtml.html.length / 1024).toFixed(0)} KB
                </div>
              </div>
              <div className="flex gap-2 items-center shrink-0">
                <div className="flex rounded border text-xs overflow-hidden">
                  <button
                    onClick={() => setHtmlMode('rendered')}
                    className={`px-2 py-1 ${htmlMode === 'rendered' ? 'bg-indigo-600 text-white' : ''}`}
                  >
                    Rendered
                  </button>
                  <button
                    onClick={() => setHtmlMode('source')}
                    className={`px-2 py-1 ${htmlMode === 'source' ? 'bg-indigo-600 text-white' : ''}`}
                  >
                    Source
                  </button>
                </div>
                <a
                  href={viewHtml.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-indigo-600 hover:underline"
                >
                  Open live ↗
                </a>
                <button onClick={() => setViewHtml(null)} className="icon-btn text-2xl">
                  ×
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {htmlMode === 'rendered' ? (
                <iframe
                  title="sample"
                  sandbox=""
                  srcDoc={viewHtml.html}
                  className="w-full h-full border-0"
                />
              ) : (
                <pre className="text-[11px] p-3 whitespace-pre-wrap break-all">
                  {viewHtml.html}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SpecVersions({
  parserId,
  currentSpec,
  onRestored,
  onPreview,
}: {
  parserId: number;
  currentSpec: any;
  onRestored: (d: SiteParserDetail) => void;
  onPreview: (spec: any) => void;
}) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<SiteParserSpecVersion[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [err, setErr] = useState('');

  // Fetch on expand, and again whenever the spec changes while open — a build or a chat
  // round writes a new version, and a stale list is worse than none when the whole point
  // is knowing what you can go back to.
  const load = useCallback(async () => {
    try {
      setVersions(await api.listSiteParserSpecVersions(parserId));
    } catch (e: any) {
      setErr(e.message);
    }
  }, [parserId]);
  useEffect(() => {
    if (open) load();
  }, [open, load, currentSpec]);

  const restore = async (v: number) => {
    setBusy(v);
    setErr('');
    try {
      onRestored(await api.restoreSiteParserSpecVersion(parserId, v));
      await load();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <details
      className="mt-2 text-xs"
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer text-gray-600 dark:text-gray-300 select-none">
        Versions{versions ? ` (${versions.length})` : ''} - roll back to an earlier spec
      </summary>
      {err && <p className="text-red-600 mt-1">{err}</p>}
      {versions?.length === 0 && (
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          No history yet - the next edit or AI build records one.
        </p>
      )}
      <ul className="mt-1 divide-y dark:divide-gray-700 border dark:border-gray-700 rounded">
        {(versions || []).map((v) => (
          <li
            key={v.version}
            className={`flex items-center gap-2 px-2 py-1.5 ${
              v.is_current ? 'bg-green-50 dark:bg-green-950' : ''
            }`}
          >
            <span className="font-mono w-8">v{v.version}</span>
            <span className="text-gray-500 dark:text-gray-400 w-32 shrink-0">
              {new Date(v.created_at).toLocaleString()}
            </span>
            <span className="w-10">{v.authored_by === 'HUMAN' ? '👤' : '🤖'}</span>
            <span className="flex-1 truncate" title={v.note || ''}>
              {v.note || '-'}
            </span>
            <span className="text-gray-500 dark:text-gray-400 shrink-0">
              {v.field_count} field{v.field_count === 1 ? '' : 's'}
              {/* A score is only present when something back-tested this spec; "—" means
                  unmeasured, NOT zero. */}
              {v.score != null ? ` · score ${v.score}` : ''}
            </span>
            {v.is_current ? (
              <span className="text-green-700 dark:text-green-400 shrink-0">current</span>
            ) : (
              <>
                <button
                  onClick={() => onPreview(v.spec)}
                  className="px-1.5 py-0.5 rounded border dark:border-gray-600 shrink-0"
                  title="Load this spec into the editor above without saving it"
                >
                  Preview
                </button>
                <button
                  onClick={() => restore(v.version)}
                  disabled={busy !== null}
                  className="px-1.5 py-0.5 rounded bg-amber-600 text-white disabled:opacity-50 shrink-0"
                  title="Make this the parser's spec. Recorded as a new version, so it's undoable. Drops to DRAFT and clears approvals."
                >
                  {busy === v.version ? 'Restoring…' : 'Restore'}
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

function BuilderChat({
  parserId,
  focusSource,
  onSpecMaybeChanged,
  building,
  hasFields,
  selectedSampleCount,
  usableRawCount,
  onBuild,
  buildBusy,
}: {
  parserId: number;
  focusSource: string;
  onSpecMaybeChanged: () => void;
  building: boolean;
  hasFields: boolean;
  selectedSampleCount: number;
  usableRawCount: number;
  onBuild: () => void;
  buildBusy: boolean;
}) {
  const [chat, setChat] = useState<ParserChat | null>(null);
  const [input, setInput] = useState('');
  const pollRef = useRef<any>(null);

  const load = useCallback(async () => {
    try {
      const c = await api.getSiteParserChat(parserId);
      setChat(c);
      return c;
    } catch {
      return null;
    }
  }, [parserId]);

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);

  // A build started elsewhere (the Rebuild button, or a candidate build queued by
  // discovery) runs the SAME agent and writes into this chat. Without this the panel
  // sits stale until something else re-renders it, and the build looks like it went
  // nowhere; with it, the transcript is the one place a build is visible.
  const { subscribe: subscribeChat } = useWebSocket();
  useEffect(() => {
    return subscribeChat((event: any) => {
      if (event?.event_type === 'parser_builder_chat' && event.parser_id === parserId) {
        load();
      }
    });
  }, [subscribeChat, parserId, load]);

  // Poll while the agent is thinking, as a fallback for a missed event. Polling ONLY
  // refetches — see the transition effect below for the "it finished" notification.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (chat?.status === 'thinking' || building) {
      pollRef.current = setInterval(load, 2000);
    }
    return () => clearInterval(pollRef.current);
  }, [chat?.status, building, load]);

  // Tell the parent a round finished, keyed off the thinking -> idle TRANSITION rather
  // than off whoever noticed it. This used to live inside the poll callback, which the
  // websocket then raced and won: the event's load() flipped status to idle, the poll
  // effect re-ran, cleanup cleared the interval, and the callback never observed the
  // change — so the spec editor kept showing the pre-edit spec until a page refresh.
  const wasWorkingRef = useRef(false);
  useEffect(() => {
    const working = chat?.status === 'thinking' || building;
    if (wasWorkingRef.current && !working) onSpecMaybeChanged();
    wasWorkingRef.current = working;
  }, [chat?.status, building, onSpecMaybeChanged]);

  const [clearing, setClearing] = useState(false);

  const clearChat = async () => {
    // No confirm: nothing is destroyed — the spec stays and the old messages are still
    // in the DB under the previous session number.
    setClearing(true);
    try {
      setChat(await api.clearSiteParserChat(parserId));
    } catch (e: any) {
      alert(e.message);
    } finally {
      setClearing(false);
    }
  };

  const send = async (text?: string) => {
    const body = (text ?? input).trim();
    if (!body) return;
    if (text === undefined) setInput('');
    try {
      setChat(
        await api.sendSiteParserChat(
          parserId,
          body,
          focusSource === 'all' ? undefined : focusSource,
        ),
      );
    } catch (e: any) {
      alert(e.message);
    }
  };

  // Canned openers. Most of a parser's lifecycle is a handful of questions, and a blank
  // chat box doesn't tell you that the agent can read the page for you at all — these
  // ARE the discoverability. Order matters: the first chip is whatever this parser needs
  // next (write the fields when there are none, verify them when there are).
  const thinking = chat?.status === 'thinking' || building;
  const canBuild = selectedSampleCount > 0 && !building;
  const prompts: {
    label: string;
    title: string;
    run: () => void;
    primary?: boolean;
  }[] = [
    {
      label: hasFields
        ? `✨ Rebuild the fields${selectedSampleCount ? ` (${selectedSampleCount} samples)` : ''}`
        : `✨ Build this parser with AI${selectedSampleCount ? ` (${selectedSampleCount} samples)` : ''}`,
      title: canBuild
        ? 'The agent explores the checked samples, writes the fields and back-tests them'
        : usableRawCount === 0
          ? 'No raw sample to learn from - the loaded pages are cleaned HTML with the attributes stripped. Use ‘Re-capture via browser’ or paste raw HTML in Step 1.'
          : 'Check one or more raw ✓ samples in Step 1 first',
      run: onBuild,
      primary: true,
    },
    {
      label: "🔎 What's on this page?",
      title: 'Have the agent map the sample and describe what can be extracted',
      run: () =>
        send(
          'Outline the sample page and tell me, in plain English, what content is on ' +
            'it that would be worth extracting - and what you would name each field.',
        ),
    },
    {
      label: '🧪 Test the current spec',
      title: 'Back-test the saved spec across the samples and explain the result',
      run: () =>
        send(
          'Run the back-test on the current spec and explain the result - which ' +
            'fields are filling, which are empty, and why.',
        ),
    },
    {
      label: '🕳️ What am I missing?',
      title: 'Ask the reviewer whether the substantive content is being captured',
      run: () =>
        send(
          'Check whether this spec captures the substantive content a writer would ' +
            'cite (the reviews / comments / body), not just metadata. If something is ' +
            'missing, add fields for it and back-test.',
        ),
    },
  ];

  return (
    <section>
      {/* Subordinate to the "Step 2 ·" heading this now sits under — same weight would
          read as a competing step rather than the way you fill this one in. */}
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-sm font-medium">
          🤖 Builder agent{' '}
          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
            {focusSource === 'all'
              ? '(all sources)'
              : `(focused on ${focusSource} samples)`}
          </span>
        </h4>
        {(chat?.messages || []).length > 0 && (
          <button
            onClick={clearChat}
            disabled={thinking || clearing}
            title="Start a fresh session: hides this transcript so the next round is prompted from a clean history. Your spec and the old messages are kept."
            className="px-2 py-0.5 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            {clearing ? 'Clearing…' : 'Clear chat'}
          </button>
        )}
      </div>
      <div className="border dark:border-gray-700 rounded p-2 h-56 overflow-y-auto bg-gray-50 dark:bg-gray-800 text-sm space-y-2">
        {(chat?.messages || [])
          .filter((m) => m.entity_type === 'HUMAN' || m.entity_type === 'AI')
          .map((m) => {
            // A tool-only turn has no text; show WHICH tool ran (e.g. "🔧 update_spec")
            // instead of a bare "…acting…" so the transcript reads as a sequence of actions.
            const toolNames = (m.tool_calls || [])
              .map((tc: any) => tc?.function?.name || tc?.name)
              .filter(Boolean);
            return (
              <div
                key={m.message_number}
                className={m.entity_type === 'HUMAN' ? 'text-right' : ''}
              >
                {m.content ? (
                  <span
                    className={`inline-block px-2 py-1 rounded whitespace-pre-wrap ${
                      m.entity_type === 'HUMAN'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white dark:bg-gray-900 border dark:border-gray-700'
                    }`}
                  >
                    {m.content}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                    {toolNames.length ? `🔧 ${toolNames.join(', ')}` : '…acting…'}
                  </span>
                )}
              </div>
            );
          })}
        {thinking && (
          <div className="text-gray-400 dark:text-gray-500 italic">builder is working…</div>
        )}
        {chat?.status === 'failed' && (
          <div className="text-red-600 text-xs">{chat.last_error}</div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {prompts.map((p) => {
          const disabled = thinking || (p.primary ? !canBuild : false);
          return (
            <button
              key={p.label}
              onClick={p.run}
              disabled={disabled || (p.primary && buildBusy)}
              title={p.title}
              className={`px-2 py-1 text-xs rounded border disabled:opacity-50 ${
                p.primary
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {p.primary && buildBusy ? 'Queuing…' : p.label}
            </button>
          );
        })}
      </div>
      {/* A greyed chip with its reason hidden in a tooltip reads as "broken" — say it. */}
      {!building && !canBuild && (
        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
          {usableRawCount === 0
            ? 'To build with AI you need a raw sample: the loaded pages are cleaned crawl4ai HTML (attributes stripped, nothing to write selectors against). Use ‘Re-capture via browser’ or paste raw HTML in Step 1.'
            : 'To build with AI, check one or more of the raw ✓ samples in Step 1 for it to learn from.'}
        </p>
      )}
      <div className="flex gap-2 mt-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="e.g. also grab the review count; loosen the URL match"
          className="flex-1 border rounded px-2 py-1.5 text-sm"
          disabled={thinking}
        />
        <button
          onClick={() => send()}
          disabled={thinking}
          className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white disabled:opacity-50"
        >
          Send
        </button>
      </div>
    </section>
  );
}
